import { ReactNode } from 'react';
import { Box } from '@spark-web/box';
import { CheckCircleIcon, ExclamationIcon } from '@spark-web/icon';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { TextList } from '@spark-web/text-list';

export type IncomeVerificationUploadedDocument = {
  name: string;
  status?: 'passed' | 'failed';
};

type IncomeVerificationErrorPanelProps = {
  title: string;
  description?: ReactNode;
  descriptionItems?: ReactNode[];
  callout?: ReactNode;
  guidanceTitle?: string;
  guidanceItems?: string[];
  uploadedDocuments?: IncomeVerificationUploadedDocument[];
};

export function IncomeVerificationErrorPanel({
  title,
  description,
  descriptionItems = [],
  callout,
  guidanceTitle,
  guidanceItems = [],
  uploadedDocuments = [],
}: IncomeVerificationErrorPanelProps) {
  return (
    <Box padding="small">
      <Stack gap="large">
        <Box>
          <ExclamationIcon size="large" tone="caution" />
        </Box>
        <Stack gap="xxlarge" marginBottom="large">
          <Text weight="semibold" size="xlarge">
            {title}
          </Text>
          {descriptionItems.length ? (
            <Stack gap="xlarge">
              {descriptionItems.map((item, index) => (
                <Text key={`description-${index}`} tone="muted">
                  {item}
                </Text>
              ))}
            </Stack>
          ) : description ? (
            <Text tone="muted">{description}</Text>
          ) : null}
        </Stack>
        {callout ? (
          <Box background="infoMuted" padding="large" borderRadius="medium">
            <Text tone="muted">{callout}</Text>
          </Box>
        ) : null}
        {guidanceTitle || guidanceItems.length ? (
          <Box background="infoMuted" padding="large" borderRadius="medium" marginBottom="small">
            <Stack gap="xlarge">
              {guidanceTitle ? (
                <Text weight="semibold" size="standard" tone="neutral">
                  {guidanceTitle}
                </Text>
              ) : null}
              {guidanceItems.length ? (
                <TextList type="bullet" gap="large">
                  {guidanceItems.map(item => (
                    <Text key={item} size="small">
                      {item}
                    </Text>
                  ))}
                </TextList>
              ) : null}
            </Stack>
          </Box>
        ) : null}
        {uploadedDocuments.length ? (
          <Stack gap="medium" marginTop="medium">
            <Text weight="semibold" size="standard">
              Uploaded documents
            </Text>
            {uploadedDocuments.map(document => (
              <Box key={document.name} display="flex" alignItems="center" gap="xsmall">
                {document.status === 'passed' ? (
                  <CheckCircleIcon size="xsmall" tone="positive" />
                ) : (
                  <ExclamationIcon size="xsmall" tone="caution" />
                )}
                <Text tone={document.status === 'passed' ? 'positive' : 'caution'} size="small">
                  {document.name}
                </Text>
              </Box>
            ))}
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
}
